/**
 * ChatWidgetHistoryItem Component
 * Individual chat item in the widget history list
 * Simplified version for the narrow widget layout
 */

import { MessageSquare } from "lucide-react"
import { memo } from "react"
import type { ChatHistoryApiItem } from "@taqat/core"
import { cn } from "@/lib/utils"

/**
 * Animation state for typing effect
 */
interface AnimatingTitle {
  title: string
  visibleChars: number
}

/**
 * PureChatWidgetHistoryItem component props
 */
interface ChatWidgetHistoryItemProps {
  /**
   * Chat conversation data
   */
  chat: ChatHistoryApiItem
  /**
   * Whether this chat is currently active/selected
   */
  isActive: boolean
  /**
   * Callback when chat is selected
   */
  onSelect: (chat: ChatHistoryApiItem) => void
  /**
   * Optional animation state for typing effect
   */
  animatingTitle?: AnimatingTitle
}

/**
 * PureChatWidgetHistoryItem Component
 * Renders a single chat history item with hover and active states
 *
 * @param props - Component props
 * @returns JSX element representing a chat history item
 */
const PureChatWidgetHistoryItem = ({
  chat,
  isActive,
  onSelect,
  animatingTitle,
}: ChatWidgetHistoryItemProps) => {
  return (
    <button
      className={cn(
        "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors",
        isActive
          ? "bg-primary/10 font-medium text-primary"
          : "text-foreground hover:bg-accent"
      )}
      onClick={() => onSelect(chat)}
      type="button"
    >
      <MessageSquare className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
      <span className="flex-1 truncate">
        {animatingTitle ? (
          <>
            {animatingTitle.title.slice(0, animatingTitle.visibleChars)}
            <span className="animate-pulse">|</span>
          </>
        ) : (
          chat.title
        )}
      </span>
    </button>
  )
}

/**
 * Memoized ChatWidgetHistoryItem component
 * Re-renders when chat ID, title, active state, or animation state changes
 */
export const ChatWidgetHistoryItem = memo(
  PureChatWidgetHistoryItem,
  (prevProps, nextProps) =>
    prevProps.chat.id === nextProps.chat.id &&
    prevProps.chat.title === nextProps.chat.title &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.animatingTitle?.visibleChars === nextProps.animatingTitle?.visibleChars
)
